import type {
  SyncBootstrapSnapshot,
  SyncCatchupResponse,
  SyncClientMessage,
  SyncPushRequest,
  SyncPushResponse,
  SyncServerMessage,
} from '@couplespace/sync';

import { getDevAuthHeaders } from '../auth/dev-impersonation';
import { getNativeBearerAuthHeaders } from '../auth/native-api-auth';
import { getNativeSessionToken } from '../auth/capacitor-session';
import { getApiBaseUrl } from '../env';

async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const base = getApiBaseUrl().replace(/\/$/, '');
  return fetch(`${base}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Client-Schema-Version': '1',
      ...getDevAuthHeaders(),
      ...(await getNativeBearerAuthHeaders()),
      ...init?.headers,
    },
  });
}

async function parseJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

export async function fetchBootstrap(): Promise<SyncBootstrapSnapshot> {
  const res = await apiFetch('/rpc/sync/bootstrap');
  return parseJson(res);
}

export async function fetchCatchup(since: number): Promise<SyncCatchupResponse> {
  const res = await apiFetch(`/rpc/sync/catchup?since=${since}`);
  return parseJson(res);
}

export async function pushChanges(changes: SyncPushRequest['changes']): Promise<SyncPushResponse> {
  const res = await apiFetch('/rpc/sync/push', {
    method: 'POST',
    body: JSON.stringify({ changes }),
  });
  return parseJson(res);
}

export async function getSyncWebSocketUrl(): Promise<string> {
  const base = getApiBaseUrl().replace(/\/$/, '');
  const wsBase = base.replace(/^http/, 'ws');
  const token = await getNativeSessionToken();
  if (token) {
    return `${wsBase}/ws/sync?token=${encodeURIComponent(token)}`;
  }
  return `${wsBase}/ws/sync`;
}

export type { SyncClientMessage, SyncServerMessage };
